import type { TFunction } from 'i18next';
import type { StudentReportItem } from '@/school-management/types/response';
import type { FilterValue } from '@/components/school-management/BimloaFilter/BimloaFilter';
import { DateFilterValue } from '@/components/school-management/BimloaFilter/BimloaFilter';
import { NumberRangeFilterValue } from '@/components/school-management/BimloaFilter/NumberRangeFilter';
import { convertDateFilter } from '@/school-management/utils/filterUtils';
import {
  StudentAutocompleteItem,
  GroupAutocompleteItem,
} from '@/school-management/types/response/autocomplete';
import { UserStatusEnum } from '@/school-management/types/response/studentDetails';

function parseIsoDate(iso?: string | null): Date | null {
  if (!iso || iso === 'N/A') return null;
  const d = new Date(iso.toUpperCase().endsWith('Z') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Client-side filtering for {@link StudentReportItem} rows (teacher / overview data source).
 * Mirrors the admin report filters where row fields exist.
 */
export function filterStudentReportItems(
  items: StudentReportItem[],
  filters: Record<string, FilterValue> | undefined,
  t: TFunction
): StudentReportItem[] {
  if (!filters) return items;

  let out = items;

  const nameSel = filters.name;
  if (Array.isArray(nameSel) && nameSel.length > 0) {
    const ids = new Set((nameSel as StudentAutocompleteItem[]).map(s => s.id));
    out = out.filter(r => ids.has(r.userId));
  }

  const groupSel = filters.groupName;
  if (Array.isArray(groupSel) && groupSel.length > 0) {
    const gids = new Set((groupSel as GroupAutocompleteItem[]).map(g => g.groupId));
    out = out.filter(r => r.groupId && gids.has(r.groupId));
  }

  const status = ((filters.status || []) as string[]).filter(v => v && v !== 'All');
  if (status.length > 0) {
    const statusMapper: Record<string, UserStatusEnum[]> = {
      [t('active')]: [
        UserStatusEnum.ACTIVE,
        UserStatusEnum.LEVEL_DOWN,
        UserStatusEnum.LEVEL_UP,
        UserStatusEnum.LEVEL_COMPLETED_BLOCKED,
      ],
      [t('inactive')]: [UserStatusEnum.INACTIVE],
      [t('levelTestIncomplete')]: [UserStatusEnum.LEVEL_TEST_INCOMPLETE],
      [t('resetPassword')]: [UserStatusEnum.RESET_PASSWORD],
    };
    const allowed = new Set(status.flatMap(s => statusMapper[s] || []));
    out = out.filter(r => r.userStatus != null && allowed.has(r.userStatus));
  }

  const startingLevel = ((filters.startingLevel || []) as string[])
    .filter(v => v && v !== 'All')
    .map(v => Number(v));
  if (startingLevel.length > 0) {
    const allow = new Set(startingLevel);
    out = out.filter(r => allow.has(r.startingLevel));
  }

  const currentLevel = ((filters.currentLevel || []) as string[])
    .filter(v => v && v !== 'All')
    .map(v => Number(v));
  if (currentLevel.length > 0) {
    const allow = new Set(currentLevel);
    out = out.filter(r => allow.has(r.currentLevel));
  }

  const ca = filters.completedActivities;
  if (
    ca &&
    typeof ca === 'object' &&
    !Array.isArray(ca) &&
    (typeof (ca as NumberRangeFilterValue).min === 'number' ||
      typeof (ca as NumberRangeFilterValue).max === 'number')
  ) {
    const rangeFilter = ca as NumberRangeFilterValue;
    const min = typeof rangeFilter.min === 'number' ? rangeFilter.min : 0;
    const max = typeof rangeFilter.max === 'number' ? rangeFilter.max : Number.POSITIVE_INFINITY;
    out = out.filter(r => r.completedActivities >= min && r.completedActivities <= max);
  }

  const scoreFilter = filters.score;
  if (
    scoreFilter &&
    typeof scoreFilter === 'object' &&
    !Array.isArray(scoreFilter) &&
    (typeof (scoreFilter as NumberRangeFilterValue).min === 'number' ||
      typeof (scoreFilter as NumberRangeFilterValue).max === 'number')
  ) {
    const rangeFilter = scoreFilter as NumberRangeFilterValue;
    const min = typeof rangeFilter.min === 'number' ? rangeFilter.min : 0;
    const max = typeof rangeFilter.max === 'number' ? rangeFilter.max : 100;
    out = out.filter(r => {
      const score = r.averageScore;
      if (score == null || score < 0) return false;
      return score >= min && score <= max;
    });
  }

  if (
    filters.firstLogin &&
    typeof filters.firstLogin === 'object' &&
    'mode' in filters.firstLogin
  ) {
    const dateFilter = convertDateFilter(filters.firstLogin as DateFilterValue);
    if (dateFilter?.range) {
      const from = new Date(dateFilter.range.from).getTime();
      const to = new Date(dateFilter.range.to).getTime();
      out = out.filter(r => {
        const d = parseIsoDate(r.firstLogin);
        if (!d) return false;
        const tt = d.getTime();
        return tt >= from && tt <= to;
      });
    }
  }

  if (filters.lastLogin && typeof filters.lastLogin === 'object' && 'mode' in filters.lastLogin) {
    const dateFilter = convertDateFilter(filters.lastLogin as DateFilterValue);
    if (dateFilter?.range) {
      const from = new Date(dateFilter.range.from).getTime();
      const to = new Date(dateFilter.range.to).getTime();
      out = out.filter(r => {
        const d = parseIsoDate(r.lastLogin);
        if (!d) return false;
        const tt = d.getTime();
        return tt >= from && tt <= to;
      });
    }
  }

  return out;
}

const sortKeys = new Set([
  'name',
  'teacherNames',
  'groupName',
  'gradeId',
  'startingLevel',
  'currentLevel',
  'completedActivities',
  'timeSpentSeconds',
  'firstLogin',
  'lastLogin',
  'averageScore',
]);

export function sortStudentReportItems(
  items: StudentReportItem[],
  sortBy: string,
  direction: 'asc' | 'desc'
): StudentReportItem[] {
  if (!sortKeys.has(sortBy)) return [...items];

  const mul = direction === 'asc' ? 1 : -1;
  const cmpStr = (a: string, b: string) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }) * mul;
  const cmpNum = (a: number, b: number) => (a === b ? 0 : a < b ? -mul : mul);

  return [...items].sort((x, y) => {
    switch (sortBy) {
      case 'name':
        return cmpStr(x.name || '', y.name || '');
      case 'groupName':
        return cmpStr(x.groupName || '', y.groupName || '');
      case 'gradeId':
        return cmpNum(x.gradeId ?? -1, y.gradeId ?? -1);
      case 'startingLevel':
        return cmpNum(x.startingLevel, y.startingLevel);
      case 'currentLevel':
        return cmpNum(x.currentLevel, y.currentLevel);
      case 'completedActivities':
        return cmpNum(x.completedActivities, y.completedActivities);
      case 'timeSpentSeconds':
        return cmpNum(x.timeSpentSeconds, y.timeSpentSeconds);
      case 'firstLogin': {
        const a = parseIsoDate(x.firstLogin)?.getTime() ?? 0;
        const b = parseIsoDate(y.firstLogin)?.getTime() ?? 0;
        return cmpNum(a, b);
      }
      case 'lastLogin': {
        const a = parseIsoDate(x.lastLogin)?.getTime() ?? 0;
        const b = parseIsoDate(y.lastLogin)?.getTime() ?? 0;
        return cmpNum(a, b);
      }
      case 'averageScore': {
        const a = x.averageScore ?? -1;
        const b = y.averageScore ?? -1;
        return cmpNum(a, b);
      }
      case 'teacherNames': {
        const sa = (x.teachers || []).map(t => `${t.firstName} ${t.lastName}`).join(', ');
        const sb = (y.teachers || []).map(t => `${t.firstName} ${t.lastName}`).join(', ');
        return cmpStr(sa, sb);
      }
      default:
        return 0;
    }
  });
}
